import { createContext, useContext } from 'react';
import { createTranslator, dirOf, lt, type Dictionary, type Locale } from '@cfg/shared';

export const DICT = {
  appTitle: lt('FORM / studio', 'FORM / סטודיו'),
  appSubtitle: lt('Spaces made personal', 'חללים בהתאמה אישית'),
  scene: lt('View', 'זווית'),
  scenes: lt('Views', 'זוויות'),
  day: lt('Day', 'יום'),
  night: lt('Evening', 'ערב'),
  lighting: lt('Lighting', 'תאורה'),
  zones: lt('Zones', 'אזורים'),
  chooseZone: lt('Choose what to change', 'בחרו מה לשנות'),
  materials: lt('Materials', 'חומרים'),
  search: lt('Search materials', 'חיפוש חומרים'),
  all: lt('All', 'הכל'),
  noResults: lt('No materials match your filters.', 'אין חומרים שתואמים לסינון.'),
  code: lt('Code', 'קוד'),
  sku: lt('SKU', 'מק"ט'),
  collection: lt('Collection', 'קולקציה'),
  finish: lt('Finish', 'גימור'),
  tileSize: lt('Tile size', 'גודל אריח'),
  inStock: lt('In stock', 'במלאי'),
  lowStock: lt('Low stock', 'מלאי נמוך'),
  outOfStock: lt('Out of stock', 'אזל מהמלאי'),
  onRequest: lt('On request', 'לפי בקשה'),
  unavailable: lt('Currently unavailable', 'אינו זמין כרגע'),
  share: lt('Share', 'שיתוף'),
  shareTitle: lt('Share this configuration', 'שיתוף התצורה'),
  shareHint: lt(
    'Anyone with this link can open the configuration.',
    'כל מי שיש לו את הקישור יוכל לפתוח את התצורה.',
  ),
  copy: lt('Copy link', 'העתקת קישור'),
  copied: lt('Link copied', 'הקישור הועתק'),
  exportPdf: lt('Spec sheet (PDF)', 'גיליון מפרט (PDF)'),
  exportTitle: lt('Export specification sheet', 'הפקת גיליון מפרט'),
  exportHint: lt(
    'A PDF with the renders and the material specification.',
    'קובץ PDF עם ההדמיות ומפרט החומרים.',
  ),
  projectName: lt('Project name', 'שם הפרויקט'),
  clientName: lt('Client', 'לקוח'),
  designerName: lt('Designer', 'מעצב/ת'),
  company: lt('Company', 'חברה'),
  phone: lt('Phone', 'טלפון'),
  email: lt('Email', 'אימייל'),
  notes: lt('Notes', 'הערות'),
  designerLogo: lt('Designer logo (optional)', 'לוגו המעצב/ת (לא חובה)'),
  chooseLogo: lt('Choose image', 'בחירת תמונה'),
  removeLogo: lt('Remove', 'הסרה'),
  logoHint: lt(
    'PNG, JPEG or WebP. Shown at the top of the PDF.',
    'PNG, JPEG או WebP. מוצג בראש קובץ ה-PDF.',
  ),
  logoInvalid: lt('This image cannot be used as a logo.', 'לא ניתן להשתמש בתמונה זו כלוגו.'),
  includeViews: lt('Include views', 'זוויות לכלול'),
  includeLighting: lt('Include lighting', 'תאורה לכלול'),
  generate: lt('Generate PDF', 'הפקת PDF'),
  generating: lt('Preparing your PDF…', 'מכינים את קובץ ה-PDF…'),
  exportDone: lt('PDF downloaded', 'קובץ ה-PDF הורד'),
  downloadImage: lt('Download image', 'הורדת תמונה'),
  summary: lt('Summary', 'סיכום'),
  summaryTitle: lt('Your selection', 'הבחירות שלך'),
  reset: lt('Reset to default', 'איפוס לברירת המחדל'),
  zoomIn: lt('Zoom in', 'הגדלה'),
  zoomOut: lt('Zoom out', 'הקטנה'),
  fitView: lt('Fit to view', 'התאמה לתצוגה'),
  fullscreen: lt('Full screen', 'מסך מלא'),
  close: lt('Close', 'סגירה'),
  cancel: lt('Cancel', 'ביטול'),
  loading: lt('Loading…', 'טוען…'),
  loadingScene: lt('Loading the scene…', 'טוען את ההדמיה…'),
  errorCatalog: lt(
    'The catalog could not be loaded. Please try again.',
    'לא ניתן לטעון את הקטלוג. נסו שוב.',
  ),
  retry: lt('Try again', 'נסו שוב'),
  errorGeneric: lt('Something went wrong. Please try again.', 'משהו השתבש. נסו שוב.'),
  webglMissing: lt(
    'Your browser cannot display the interactive view. You can still browse materials and export a spec sheet.',
    'הדפדפן אינו תומך בתצוגה האינטראקטיבית. עדיין אפשר לעיין בחומרים ולהפיק גיליון מפרט.',
  ),
  contextLost: lt(
    'The interactive view was interrupted by the graphics system. Restoring it…',
    'התצוגה האינטראקטיבית נקטעה על ידי מערכת הגרפיקה. משחזרים אותה…',
  ),
  viewer: lt('Interactive view', 'תצוגה אינטראקטיבית'),
  viewerKeys: lt(
    'Press + or − to zoom and 0 to fit the view. When zoomed in, use the arrow keys to move around.',
    'הקישו + או − להגדלה ולהקטנה ו־0 להתאמה לתצוגה. בזמן הגדלה אפשר לנוע בתמונה עם מקשי החיצים.',
  ),
  studioNav: lt('Product studio', 'סטודיו המוצרים'),
  navKitchens: lt('Kitchens', 'מטבחים'),
  navDoors: lt('Doors', 'דלתות'),
  navDemo: lt('Demo', 'הדגמה'),
  navAdmin: lt('Admin', 'ניהול'),
  configNotFound: lt('The shared configuration was not found.', 'התצורה המשותפת לא נמצאה.'),
  selected: lt('Selected', 'נבחר'),
  sceneAlt: lt('Kitchen visualisation: {desc}', 'הדמיית מטבח: {desc}'),
  language: lt('עברית', 'English'),
  switchLanguage: lt('Switch language', 'החלפת שפה'),
  disclaimer: lt(
    'Visualisation for illustration. Natural materials vary — confirm with physical samples.',
    'ההדמיה להמחשה בלבד. חומרים טבעיים משתנים — יש לאשר מול דוגמאות.',
  ),
  materialChanged: lt('{zone}: {material}', '{zone}: {material}'),
  hoverHint: lt(
    'Tip: click a surface in the image to select it.',
    'טיפ: לחצו על משטח בתמונה כדי לבחור אותו.',
  ),
  filters: lt('Filters', 'סינון'),
  colorFamily: lt('Colour', 'צבע'),
  inStockOnly: lt('In stock only', 'רק מהמלאי'),
  color_white: lt('White', 'לבן'),
  color_beige: lt('Beige', 'בז׳'),
  color_wood: lt('Wood tones', 'גווני עץ'),
  color_brown: lt('Brown', 'חום'),
  color_grey: lt('Grey', 'אפור'),
  color_black: lt('Black', 'שחור'),
  color_green: lt('Green', 'ירוק'),
  color_blue: lt('Blue', 'כחול'),
  color_red: lt('Red', 'אדום'),
  color_pink: lt('Pink', 'ורוד'),
  color_yellow: lt('Yellow', 'צהוב'),
  type_wood: lt('Wood', 'עץ'),
  type_lacquer: lt('Lacquer', 'לכה'),
  type_laminate: lt('Laminate', 'למינציה'),
  type_stone: lt('Stone', 'אבן'),
  type_ceramic: lt('Ceramic', 'קרמיקה'),
  type_concrete: lt('Concrete', 'בטון'),
  type_metal: lt('Metal', 'מתכת'),
  finish_matte: lt('Matte', 'מט'),
  finish_satin: lt('Satin', 'סאטן'),
  finish_gloss: lt('Gloss', 'מבריק'),
} satisfies Dictionary;

export type DictKey = keyof typeof DICT;
export type Translate = ReturnType<typeof createTranslator<typeof DICT>>;

export interface I18n {
  locale: Locale;
  dir: 'rtl' | 'ltr';
  t: Translate;
}

export function makeI18n(locale: Locale): I18n {
  return { locale, dir: dirOf(locale), t: createTranslator(DICT, locale) };
}

export const I18nContext = createContext<I18n>(makeI18n('he'));
export const useI18n = () => useContext(I18nContext);

const STORAGE_KEY = 'cfg-kitchen-locale';

export function initialLocale(): Locale {
  const fromUrl = new URLSearchParams(location.search).get('lang');
  if (fromUrl === 'en' || fromUrl === 'he') return fromUrl;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'he') return stored;
  } catch {
    // storage unavailable
  }
  return navigator.language.toLowerCase().startsWith('he') ? 'he' : 'en';
}

export function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // storage unavailable
  }
}
